import React from 'react';
import { Typography } from 'antd';
import { useAdminAccess } from '../../../contexts/AdminAccessContext';
import { Account } from '../../../types/Account';
import { mapStatusForClient, EXCLUDED_STATUSES, getStatusClass } from '../../../utils/statusMapping';
import '../../../utils/statusStyles.css';

const { Text } = Typography;

export interface StatementInfo {
  lastBillAmount: string | React.ReactNode;
  status: string | React.ReactNode;
  dueDate: string | React.ReactNode;
  loading?: boolean;
  error?: boolean;
}

export function useAccountStatementInfo(account: Account): StatementInfo {
  const { hasAdminAccess } = useAdminAccess();

  // Get the latest statement from the account
  const latestStatement = account.latestStatement;

  // If no statement exists, return empty values
  if (!latestStatement) {
    return {
      lastBillAmount: '',
      status: '',
      dueDate: ''
    };
  }

  // For non-admin users, check if the status should be excluded
  const status = latestStatement.displayStatus || '';
  if (!hasAdminAccess && EXCLUDED_STATUSES.some(excluded => 
    status.toLowerCase() === excluded.toLowerCase()
  )) {
    return {
      lastBillAmount: '',
      status: '',
      dueDate: ''
    };
  }

  // Always map the status for client display in client interface
  const displayStatus = mapStatusForClient(latestStatement.displayStatus, null);
  const statusClass = getStatusClass(displayStatus);

  return {
    lastBillAmount: new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(latestStatement.amountDue),
    status: displayStatus ? (
      <span className={statusClass}>
        {displayStatus}
      </span>
    ) : '',
    dueDate: latestStatement.dueDate 
      ? new Date(latestStatement.dueDate).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: '2-digit'
        })
      : ''
  };
}
